import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Eye,
  FileText,
  FolderInput,
  Link2,
  Share2,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';
import dayjs from 'dayjs';
import type { DocumentCategory, LibraryDocument } from '@/services/api/documentService';
import {
  FileTypeIcon,
  folderNameById,
  formatFileSize,
  kindLabel,
  type DocSortDir,
  type DocSortKey,
} from '../documentUi';

interface DocumentsFileViewProps {
  files: LibraryDocument[];
  categories: DocumentCategory[];
  viewMode: 'list' | 'grid';
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: (ids: string[]) => void;
  sortKey: DocSortKey;
  sortDir: DocSortDir;
  onSortChange: (key: DocSortKey) => void;
  canDelete: (doc: LibraryDocument) => boolean;
  deleting: boolean;
  canAdd: boolean;
  onPreview: (doc: LibraryDocument) => void;
  onOpenExternal: (doc: LibraryDocument) => void;
  onCopyLink: (doc: LibraryDocument) => void;
  onDelete: (doc: LibraryDocument) => void;
  onToggleFavorite: (doc: LibraryDocument) => void;
  onMove: (doc: LibraryDocument) => void;
  onUpload?: () => void;
  onCreateDoc?: () => void;
  onBulkCopy?: () => void;
  onBulkDelete?: () => void;
  onBulkMove?: () => void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const DocumentsFileView: React.FC<DocumentsFileViewProps> = ({
  files,
  categories,
  viewMode,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  sortKey,
  sortDir,
  onSortChange,
  canDelete,
  deleting,
  canAdd,
  onPreview,
  onOpenExternal,
  onCopyLink,
  onDelete,
  onToggleFavorite,
  onMove,
  onUpload,
  onCreateDoc,
  onBulkCopy,
  onBulkDelete,
  onBulkMove,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const fileKey = useMemo(() => files.map((f) => f.id).join(','), [files]);

  useEffect(() => {
    setPage(0);
  }, [fileKey, sortKey, sortDir]);

  const sortedFiles = useMemo(() => {
    const list = [...files];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortKey === 'size') cmp = (a.size || 0) - (b.size || 0);
      else if (sortKey === 'createdByName') {
        cmp = (a.createdByName || '').localeCompare(b.createdByName || '');
      } else {
        cmp = dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
      }
      return cmp * dir;
    });
    return list;
  }, [files, sortKey, sortDir]);

  const maxPage = Math.max(0, Math.ceil(sortedFiles.length / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);
  const pagedFiles = sortedFiles.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
  const pageIds = pagedFiles.map((f) => f.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const pagination = (
    <TablePagination
      component="div"
      count={sortedFiles.length}
      page={safePage}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
    />
  );

  const bulkBar = someSelected ? (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 2, py: 1, bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}
    >
      <Typography variant="body2" fontWeight={600}>
        {selectedIds.size} selected
      </Typography>
      <Button size="small" startIcon={<Share2 size={14} />} onClick={onBulkCopy}>
        Copy links
      </Button>
      <Button size="small" startIcon={<FolderInput size={14} />} onClick={onBulkMove}>
        Move
      </Button>
      <Button size="small" color="error" startIcon={<Trash2 size={14} />} onClick={onBulkDelete}>
        Delete
      </Button>
    </Stack>
  ) : null;

  if (files.length === 0) {
    return (
      <Card sx={{ py: 8, textAlign: 'center', boxShadow: 1, px: 2 }}>
        <FileText size={40} color="#94a3b8" />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          No documents found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {canAdd
            ? 'Upload a file or create a Google Doc in this folder.'
            : 'Select a folder shared with you to upload or add a Drive link.'}
        </Typography>
        {canAdd && (
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button variant="contained" startIcon={<Upload size={16} />} onClick={onUpload}>
              Upload
            </Button>
            <Button variant="outlined" startIcon={<FileText size={16} />} onClick={onCreateDoc}>
              New Google Doc
            </Button>
          </Stack>
        )}
      </Card>
    );
  }

  const rowActions = (doc: LibraryDocument) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <Tooltip title={doc.isFavorite ? 'Unstar' : 'Star'}>
        <IconButton size="small" onClick={() => onToggleFavorite(doc)} color={doc.isFavorite ? 'warning' : 'default'}>
          <Star size={16} fill={doc.isFavorite ? 'currentColor' : 'none'} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Preview">
        <IconButton size="small" onClick={() => onPreview(doc)}>
          <Eye size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Copy link">
        <IconButton size="small" onClick={() => onCopyLink(doc)}>
          <Share2 size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Move">
        <IconButton size="small" onClick={() => onMove(doc)}>
          <FolderInput size={16} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Open in Drive">
        <IconButton size="small" onClick={() => onOpenExternal(doc)}>
          <Link2 size={16} />
        </IconButton>
      </Tooltip>
      {canDelete(doc) && (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" disabled={deleting} onClick={() => onDelete(doc)}>
            <Trash2 size={16} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  if (viewMode === 'grid') {
    return (
      <Box>
        {bulkBar}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
            pt: bulkBar ? 2 : 0,
          }}
        >
          {pagedFiles.map((doc) => (
            <Card
              key={doc.id}
              sx={{
                p: 2,
                cursor: 'pointer',
                position: 'relative',
                outline: selectedIds.has(doc.id) ? '2px solid' : 'none',
                outlineColor: 'primary.main',
                '&:hover': { boxShadow: 3 },
              }}
              onClick={() => onPreview(doc)}
            >
              <Checkbox
                size="small"
                checked={selectedIds.has(doc.id)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleSelect(doc.id)}
                sx={{ position: 'absolute', top: 4, left: 4 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <FileTypeIcon doc={doc} size={40} />
              </Box>
              <Typography fontWeight={600} noWrap title={doc.name}>
                {doc.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(doc.size)} · {kindLabel(doc.kind, doc.mimeType)}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25, mt: 1.5, flexWrap: 'wrap' }}>
                {rowActions(doc)}
              </Box>
            </Card>
          ))}
        </Box>
        {pagination}
      </Box>
    );
  }

  const sortLabel = (key: DocSortKey, label: string) => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key ? sortDir : 'asc'}
      onClick={() => onSortChange(key)}
      sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Card sx={{ overflow: 'hidden' }}>
      {bulkBar}
      <TableContainer>
        <Table size="medium" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={!allPageSelected && pageIds.some((id) => selectedIds.has(id))}
                  onChange={() => onToggleSelectAll(pageIds)}
                />
              </TableCell>
              <TableCell>{sortLabel('name', 'Name')}</TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  Folder
                </Typography>
              </TableCell>
              <TableCell>{sortLabel('size', 'Size')}</TableCell>
              <TableCell>{sortLabel('createdByName', 'Uploaded by')}</TableCell>
              <TableCell>{sortLabel('createdAt', 'Date')}</TableCell>
              <TableCell align="right">
                <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedFiles.map((doc) => (
              <TableRow key={doc.id} hover selected={selectedIds.has(doc.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(doc.id)}
                    onChange={() => onToggleSelect(doc.id)}
                  />
                </TableCell>
                <TableCell>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', minWidth: 0 }}
                    onClick={() => onPreview(doc)}
                  >
                    <FileTypeIcon doc={doc} />
                    <Typography fontWeight={500} noWrap title={doc.name}>
                      {doc.name}
                    </Typography>
                    {doc.isFavorite && <Star size={14} color="#ed6c02" fill="#ed6c02" />}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography color="text.secondary" noWrap>
                    {folderNameById(categories, doc.categoryId)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography color="text.secondary">{formatFileSize(doc.size)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color="text.secondary">{doc.createdByName || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography color="text.secondary">
                    {doc.createdAt ? dayjs(doc.createdAt).format('DD MMM YYYY') : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{rowActions(doc)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination}
    </Card>
  );
};

export default DocumentsFileView;
